import React, { useContext, useState, useEffect, useMemo } from "react";
import { useHistory } from "react-router-dom";

import { makeStyles, createTheme, ThemeProvider } from "@material-ui/core/styles";
import { IconButton } from "@material-ui/core";
import { MoreVert, Replay, Check } from "@material-ui/icons";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import TicketOptionsMenu from "../TicketOptionsMenu";
import ButtonWithSpinner from "../ButtonWithSpinner";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { TicketsContext } from "../../context/Tickets/TicketsContextIndexedDB";
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import UndoRoundedIcon from '@material-ui/icons/UndoRounded';
import Tooltip from '@material-ui/core/Tooltip';
import { green } from '@material-ui/core/colors';
import UndoIcon from "../../assets/icon/UndoIcon";
import SuccessIcon from "../../assets/icon/SuccessIcon";
import MoreIcon from "../../assets/icon/MoreIcon";
import { toast } from "react-toastify";
import useTickets from "../../hooks/useTickets";
import ReopenTicketModal from "../ReopenTicketModal";



const useStyles = makeStyles(theme => ({
	actionButtons: {
		marginRight: 6,
		flex: "none",
		alignSelf: "center",
		display: "flex",
		alignItems: "center",
		height: 40,
		"& > *": {
			margin: theme.spacing(0),
		},
		"& .MuiIconButton-root": {
			padding: 8,
			width: 36,
			height: 36,
		},
	},
}));

const TicketActionButtonsCustom = ({ ticket: initialTicket, handleDrawerOpen, reloadTickets }) => {
	const classes = useStyles();
	const history = useHistory();
	const [anchorEl, setAnchorEl] = useState(null);
	const [loading, setLoading] = useState(false);
	const [reopenModalOpen, setReopenModalOpen] = useState(false);
	const ticketOptionsMenuOpen = Boolean(anchorEl);
	const { user } = useContext(AuthContext);
	const { 
		setCurrentTicket, 
		ticketsList,
		updateTicketInList,
		removeTicketFromList,
		setTicketsList
	} = useContext(TicketsContext);
	// Para usar las operaciones optimistas necesitamos acceso al dispatch del contexto de tickets
	// Como este componente no tiene acceso directo al dispatch, usaremos las funciones del contexto
	const optimisticOps = {
		resolveTicket: (ticketId) => {
			// Remover inmediatamente el ticket de la lista
			removeTicketFromList(ticketId);
			setTicketsList(prevTickets => prevTickets.filter(t => t.id !== ticketId));
		},
		confirmOperation: (ticketData) => {
			// La confirmación ya se maneja con la respuesta del backend
			console.log('Operación confirmada:', ticketData);
		},
		rollbackOperation: (ticketId, originalTicket) => {
			// En caso de error, restaurar el ticket en la lista
			setTicketsList(prevTickets => {
				const exists = prevTickets.find(t => t.id === ticketId);
				if (!exists) {
					return [originalTicket, ...prevTickets];
				}
				return prevTickets.map(t => t.id === ticketId ? originalTicket : t);
			});
		}
	};

	// Validar que tenemos un ticket válido
	if (!initialTicket?.id) {
		return null;
	}

	// Obtener el ticket más actualizado del contexto
	const ticket = useMemo(() => {
		const updatedTicket = ticketsList.find(t => t.id === initialTicket.id);
		return updatedTicket || initialTicket;
	}, [ticketsList, initialTicket]);

	// Estado local para forzar el status correcto en UI (evita parpadeos con datos obsoletos)
	const [localStatus, setLocalStatus] = useState(initialTicket.status);

	useEffect(() => {
		setLocalStatus(ticket?.status);
	}, [ticket?.status]);

	const handleOpenTicketOptionsMenu = e => {
		setAnchorEl(e.currentTarget);
	};

	const handleCloseTicketOptionsMenu = e => {
		setAnchorEl(null);
	};
	
	const handleOpenReopenModal = () => {
		setReopenModalOpen(true);
	};

	const handleCloseReopenModal = () => {
		setReopenModalOpen(false);
	};

	const handleReopenTicket = (updatedTicket) => {
		// Actualizar el ticket en el contexto global
		setTicketsList(prevTickets => {
			const filteredTickets = prevTickets.filter(t => t.id !== updatedTicket.id);
			return [updatedTicket, ...filteredTickets];
		});
		setCurrentTicket({ id: updatedTicket.id, uuid: updatedTicket.uuid });
		setLocalStatus(updatedTicket.status || "open");
		
		// Cerrar el modal
		setReopenModalOpen(false);
		
		// Mostrar mensaje de éxito
		toast.success("Chat reabierto correctamente.");
		
		// Navegar al ticket
		history.push(`/tickets/${updatedTicket.uuid || updatedTicket.id}`);
	};

	const handleUpdateTicketStatus = async (e, status, userId) => {
		setLoading(true);
		const ticketId = ticket.id;
		const oldStatus = ticket.status;

		// Guardar estado original para rollback
		const originalTicket = { ...ticket };

		try {
			// 🚀 FASE 1: ACTUALIZACIÓN OPTIMISTA INMEDIATA
			console.log(`🚀 FASE 1: Actualización optimista - ${oldStatus} → ${status}`);

			// Crear ticket optimista con marcadores
			const optimisticTicket = {
				...ticket,
				status: status,
				userId: userId || ticket.userId,
				_optimisticUpdate: true,
				_updateTimestamp: Date.now()
			};

			// Actualizar estado local inmediatamente (UI fluida)
			setLocalStatus(status);

			// Actualizar en contexto según la acción
			if (status === "closed") {
				// Resolver: remover de lista inmediatamente
				removeTicketFromList(ticketId);
				setCurrentTicket({ id: null, code: null });
				
				// Navegar inmediatamente (UI fluida)
				if (window.location.pathname.includes(`/tickets/${ticketId}`) ||
						(ticket?.uuid && window.location.pathname.includes(`/tickets/${ticket.uuid}`))) {
					setTimeout(() => history.push("/tickets"), 100);
				}
			} else if (status === "open") {
				// Aceptar: actualizar en lista
				updateTicketInList(optimisticTicket);
				setCurrentTicket({ id: null, code: "#open" });
			} else if (status === "pending") {
				// Devolver: actualizar en lista
				updateTicketInList(optimisticTicket);
				setCurrentTicket({ id: null, code: null });
			}

			// 🔥 FASE 2: CONFIRMACIÓN CON EL BACKEND
			console.log(`🔥 FASE 2: Confirmando con backend...`);
			const { data } = await api.put(`/tickets/${ticketId}`, {
				status: status,
				userId: userId || null,
			});

			// ✅ FASE 3: CONFIRMACIÓN (sin manipular listas manualmente)
			console.log(`✅ FASE 3: Backend confirmó - Los eventos de socket sincronizarán`);

			// Marcar como confirmado (para que TicketsList ignore el evento de socket)
			const confirmedTicket = {
				...data,
				_confirmedUpdate: true,
				_updateTimestamp: Date.now()
			};

			// Solo actualizar el contexto si NO fue removido (closed)
			if (status !== "closed") {
				updateTicketInList(confirmedTicket);
			}

			// Mostrar mensajes de éxito
			if (status === "closed") {
				toast.success("Chat cerrado correctamente");
				history.replace("/tickets");
			} else if (status === "open") {
				toast.success("Chat abierto correctamente");
			} else if (status === "pending") {
				toast.success("Chat devuelto a pendientes");
				history.push("/tickets");
			}

			setLoading(false);

		} catch (err) {
			console.error(`❌ Error actualizando ticket ${ticketId}:`, err);

			// ❌ ROLLBACK: Restaurar estado anterior
			setLocalStatus(oldStatus);

			if (status === "closed") {
				// Si habíamos removido, volver a agregar
				updateTicketInList(originalTicket);
				toast.error("Error al cerrar el chat. Se ha revertido la acción.");
			} else {
				// Restaurar estado anterior en otros casos
				updateTicketInList(originalTicket);
			}

			setLoading(false);
			toastError(err);
		}
	};

	return (
		<div className={classes.actionButtons}>
			{localStatus === "closed" && (
				<ButtonWithSpinner
					loading={loading}
					startIcon={<Replay />}
					size="small"
					onClick={handleOpenReopenModal}
				>
					{i18n.t("messagesList.header.buttons.reopen")}
				</ButtonWithSpinner>
			)}
			{localStatus === "open" && (
				<>
					<Tooltip title={i18n.t("messagesList.header.buttons.return")}>
						<IconButton onClick={e => handleUpdateTicketStatus(e, "pending", null)}>
							<UndoIcon width="22" height="22" />
						</IconButton>
					</Tooltip>
					<Tooltip title={i18n.t("messagesList.header.buttons.resolve")}>
						<IconButton onClick={e => handleUpdateTicketStatus(e, "closed", user?.id)} color="primary">
							<SuccessIcon fill={"#5BC64C"}  width="22" height="22" />
						</IconButton>
					</Tooltip>
					<IconButton onClick={handleOpenTicketOptionsMenu}>
						<MoreIcon fill="#909090" stroke="1.8" width="22" height="22" />
					</IconButton>
					<TicketOptionsMenu
						ticket={ticket}
						anchorEl={anchorEl}
						menuOpen={ticketOptionsMenuOpen}
						handleClose={handleCloseTicketOptionsMenu}
						handleDrawerOpen={handleDrawerOpen}
					/>
				</>
			)}
			{localStatus === "pending" && (
				<Tooltip title={i18n.t("messagesList.header.buttons.accept")}>
					<IconButton 
						disabled={loading}
						onClick={e => handleUpdateTicketStatus(e, "open", user?.id)}
						color="primary"
					>
						<Check />
					</IconButton>
				</Tooltip>
			)}
			<ReopenTicketModal
				open={reopenModalOpen}
				onClose={handleCloseReopenModal}
				ticket={ticket}
				onReopenTicket={handleReopenTicket}
			/>
		</div>
	);
};

export default TicketActionButtonsCustom;
